/**
 * extractSearchParams — URLSearchParams를 baseFilterSchema.parse()에 바로 넣을 수 있는
 * 순수 객체로 변환한다.
 *
 * "options[key]" 형태의 값들은 스키마의 options 필드에 정의된 키만 화이트리스트로 인정해서
 * 모아준다. 화이트리스트에 없는 옵션 키는 무시된다. 나머지 필드는 Object.fromEntries와
 * 동일하게 그대로 펼친다.
 *
 * 각 키를 스키마에 적은 타입 그대로 뽑아준다 — 스키마를 두 번 쓸 필요가 없다.
 * - z.array(...) 로 선언 → string[] / number[]
 * - z.string() 등 배열이 아닌 스키마로 선언 → 단일 값
 * - 원소(또는 값) 타입이 z.number() 계열이면 문자열을 숫자로 캐스팅한다.
 *
 * 이 스키마를 buildFilterSearchParams(schema, data)로 다시 URLSearchParams로 만들면
 * 여기서 파싱한 것과 항상 같은 포맷으로 왕복된다 (브라우저 주소창 ↔ SSR ↔ API 요청 통일).
 *
 * @example
 * const orderFilterSchema = baseFilterSchema.extend({
 *   options: z.object({
 *     type: z.array(z.string()).default([]),   // 다중 선택 → string[]
 *     categoryId: z.number().array().default([]), // 다중 선택(숫자) → number[]
 *     status: z.string().optional(),           // 단일 값 → string
 *     page: z.number().optional(),              // 단일 값(숫자) → number
 *   }).default({}),
 * });
 *
 * const { searchParams } = new URL(req.url);
 * const data = orderFilterSchema.parse(
 *   extractSearchParams(orderFilterSchema, searchParams)
 * );
 * // data.options.type       → string[]
 * // data.options.categoryId → number[]
 * // data.options.status     → string | undefined
 * // data.options.page       → number | undefined
 */
import { z } from "zod";
import { toStringArray } from "./to-string-array";
import { castValue, getOptionsShape, unwrap } from "./zod-schema-utils";

export function extractSearchParams(
    schema: z.ZodObject<z.ZodRawShape>,
    searchParams: URLSearchParams
): Record<string, unknown> {
    const optionsShape = getOptionsShape(schema);

    const input: Record<string, unknown> = {};
    for (const [key, value] of searchParams) {
        if (/^options\[.+\]$/.test(key)) continue;
        input[key] = value;
    }

    const options: Record<string, unknown> = {};
    for (const key of Object.keys(optionsShape)) {
        const bracketKey = `options[${key}]`;
        const rawValues: string[] = [];
        for (const [k, v] of searchParams) {
            // "options[key]=a" (반복/콤마) 와 axios 기본 직렬화 "options[key][0]=a" 둘 다 받는다.
            if (k === bracketKey || k.startsWith(`${bracketKey}[`)) {
                rawValues.push(v);
            }
        }

        const fieldSchema = unwrap(optionsShape[key]);

        if (fieldSchema instanceof z.ZodArray) {
            const cleaned = toStringArray(rawValues) ?? [];
            options[key] = cleaned.map((v) => castValue(v, fieldSchema.element));
        } else {
            const last = rawValues[rawValues.length - 1];
            options[key] = last === undefined ? undefined : castValue(last, fieldSchema);
        }
    }
    input.options = options;

    return input;
}
